/**
 * Final-documents fax send / status flow: loads fax artefacts, sends via SRFax,
 * and dual-writes `fax_details_id` / `form_id` to PG + Firestore.
 *
 * @module finalDocuments/hooks/useFaxSubmission
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFaxStatus } from "../../../api/postCall/getFaxStatus";
import { sendFax } from "../../../api/postCall/sendFax";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { FaxSendResponse } from "../../../data-model/faxStatusModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { logError } from "../../../utils/customLogger";
import {
  dualWriteFaxSubmissionOutput,
  FaxSubmissionOutput,
  readFaxSubmissionOutput,
} from "../../../utils/faxSubmissionOutputPersistence";
import {
  FaxSubmissionStatus,
  getCorrectStatusBasedOnFaxSubmissionStatus,
} from "../utils/faxSubmissionHelper";

/**
 * Props for {@link useFaxSubmission}.
 *
 * @property orderId - Medical PA order id
 * @property faxSubmissionStatus - Current submission status from Postgres
 * @property orgId - Organisation id scoping fax + PG writes
 */
interface UseFaxSubmissionProps {
  orderId: string | undefined;
  faxSubmissionStatus?: string;
  orgId: string;
}

/** PG update payload for merging into `submission.submission_output`. */
interface SubmissionOutputUpdate {
  submission_output: FaxSubmissionOutput;
}

/** Manages fax PDF/id loading, SRFax send, and dual-write of fax metadata. */
export const useFaxSubmission = ({
  orderId,
  faxSubmissionStatus,
  orgId,
}: UseFaxSubmissionProps) => {
  const location = useLocation();
  const [faxPdfPath, setFaxPdfPath] = useState<string>("");
  const [faxIdForNotSend, setFaxIdForNotSend] = useState<string>("");

  const isFaxSubmissionFlow = useMemo(
    () => !!location.state?.faxPdfLink,
    [location.state?.faxPdfLink],
  );

  const makeSendCall = useMemo(() => {
    const faxSubmissionStatusToReturnFalse: string[] = [
      FaxSubmissionStatus.FAX_SUBMITTED,
      FaxSubmissionStatus.FAX_ERROR,
      FaxSubmissionStatus.FAX_DELIVERED,
      FaxSubmissionStatus.FAX_FAILED,
      MedicalPaOrderStatus.COMPLETED,
      MedicalPaOrderStatus.IN_PROGRESS,
      FaxSubmissionStatus.SUBMISSION_COMPLETED,
    ];
    if (
      faxSubmissionStatus &&
      faxSubmissionStatusToReturnFalse.includes(faxSubmissionStatus)
    ) {
      return false;
    }
    return true;
  }, [faxSubmissionStatus]);

  const { runUpdate: updateSubmissionOutput } =
    useUpdateOrderDetails<SubmissionOutputUpdate>({
      orderId: orderId ?? "",
      orgId: orgId ?? "",
      tableName: SingleOrderDetailsTableName.SUBMISSION,
    });

  const { runUpdate: runOrderStatusUpdate } = useUpdateOrderDetails<{
    submission_status: string;
  }>({
    orderId: orderId ?? "",
    orgId: orgId ?? "",
    tableName: SingleOrderDetailsTableName.ORDER_STATUS,
  });

  useEffect(() => {
    if (!isFaxSubmissionFlow || !orderId) {
      return;
    }
    let isMounted = true;
    const getFaxData = async () => {
      try {
        // PG-first (`submission_output`), Firestore fallback for legacy rows.
        const result = await readFaxSubmissionOutput(orderId, orgId);
        if (isMounted) {
          setFaxIdForNotSend(
            result?.fax_details_id != null ? String(result.fax_details_id) : "",
          );
          setFaxPdfPath(result?.filled_pdf_link ?? "");
        }
      } catch (error) {
        logError(error as Error, "Error getting fax data");
      }
    };
    getFaxData();
    return () => {
      isMounted = false;
    };
  }, [orderId, orgId, isFaxSubmissionFlow]);

  /**
   * Persists the fax submission status to the Postgres `order-status` table.
   *
   * @param submissionStatus - Normalized submission status to persist
   */
  const updateFaxStatusInPostgres = useCallback(
    async (submissionStatus: string): Promise<void> => {
      await runOrderStatusUpdate({ submission_status: submissionStatus });
    },
    [runOrderStatusUpdate],
  );

  const getFaxStatusAndUpdateOrderStatus = useCallback(
    async (faxDetailsId: string, formId: string) => {
      try {
        if (!faxDetailsId) {
          toast.error(ToastMessages.FAX_ID_NOT_FOUND);
          return;
        }
        const faxStatusResponse = await getFaxStatus({
          fax_details_id: faxDetailsId,
          form_id: formId,
          org_id: orgId ?? "",
        });

        const normalizedStatus = getCorrectStatusBasedOnFaxSubmissionStatus(
          faxStatusResponse.fax_status?.sent_status ?? "",
        );
        await updateFaxStatusInPostgres(normalizedStatus);

        // Also mirror fax_status onto submission_output (PG + Firestore).
        if (orderId) {
          await dualWriteFaxSubmissionOutput({
            orderId,
            orgId,
            patch: {
              fax_status: normalizedStatus,
              fax_status_updated_at: new Date().toISOString(),
            },
            updateSubmission: updateSubmissionOutput,
          });
        }

        return faxStatusResponse;
      } catch (error) {
        toast.error(ToastMessages.ERROR_GETTING_FAX_STATUS);
      }
    },
    [orgId, orderId, updateFaxStatusInPostgres, updateSubmissionOutput],
  );

  const makeSendCallAndUpdateFirestore = useCallback(
    async (faxPdfLink: string, formId: string) => {
      let sendFaxResponse: FaxSendResponse | null = null;
      try {
        if (!orgId || !faxPdfLink || !formId) {
          toast.error(ToastMessages.ORGANISATION_ID_NOT_FOUND);
          return;
        }

        if (makeSendCall) {
          sendFaxResponse = await sendFax({
            form_id: formId,
            org_id: orgId,
            file_path: faxPdfLink,
            order_id: orderId,
          });

          if (sendFaxResponse?.fax_details_id && orderId) {
            // Dual-write fax_details_id + form_id (merge keeps filled_pdf_link).
            await dualWriteFaxSubmissionOutput({
              orderId,
              orgId,
              patch: {
                fax_details_id: sendFaxResponse.fax_details_id,
                form_id: formId,
              },
              updateSubmission: updateSubmissionOutput,
            });
          }

          await new Promise((resolve) => setTimeout(resolve, 10000));

          return await getFaxStatusAndUpdateOrderStatus(
            sendFaxResponse?.fax_details_id ?? "",
            formId,
          );
        }

        return await getFaxStatusAndUpdateOrderStatus(faxIdForNotSend, formId);
      } catch (error) {
        toast.error(ToastMessages.ERROR_SENDING_FAX);
      }
    },
    [
      orgId,
      makeSendCall,
      orderId,
      getFaxStatusAndUpdateOrderStatus,
      faxIdForNotSend,
      updateSubmissionOutput,
    ],
  );

  const submitButtonText = useMemo(() => {
    if (isFaxSubmissionFlow) {
      if (
        faxSubmissionStatus === MedicalPaOrderStatus.COMPLETED ||
        faxSubmissionStatus === MedicalPaOrderStatus.IN_PROGRESS
      ) {
        return "Continue";
      }
      return "Submit";
    }
    return "Submit";
  }, [isFaxSubmissionFlow, faxSubmissionStatus]);

  return {
    faxPdfPath,
    faxIdForNotSend,
    isFaxSubmissionFlow,
    makeSendCall,
    makeSendCallAndUpdateFirestore,
    submitButtonText,
  };
};
